import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useIsAdmin } from "@/hooks/use-is-admin";
import {
  SKILL_BODY_MAX_LENGTH,
  SKILL_DESCRIPTION_MAX_LENGTH,
  useSkills,
} from "@/hooks/use-skills";
import { cn } from "@/lib/utils";
import { ScopeBadge } from "./scope-badge";
import { SettingsPageHeader, type BreadcrumbSegment } from "./settings-page-header";

type SkillCreateMode = "personal" | "admin";

type SkillFormState = {
  name: string;
  description: string;
  body: string;
  scope: "user" | "tenant";
};

const INPUT_CLASS =
  "mt-2 w-full rounded-[10px] border border-border bg-background px-3 py-2.5 text-[14px] text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:border-ring focus:shadow-[0_0_0_3px_oklch(0.55_0.03_264/0.10)]";

export function SkillCreate({ mode = "personal" }: { mode?: SkillCreateMode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { createSkill } = useSkills();
  const defaultScope = mode === "admin" ? "tenant" : "user";
  const basePath = mode === "admin" ? "/settings/admin/skills" : "/settings/skills";
  const [form, setForm] = useState<SkillFormState>({
    name: "",
    description: "",
    body: "",
    scope: defaultScope,
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field: keyof SkillFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreate = async () => {
    if (submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await createSkill({
        name: form.name,
        description: form.description,
        body: form.body,
        scope: form.scope,
      });
      toast.success(t("settings.savedToast"));
      navigate(`${basePath}/${created.id}`, { replace: true });
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t("error.code.service_unavailable"),
      );
      setSubmitting(false);
    }
  };

  const breadcrumbs: BreadcrumbSegment[] =
    mode === "admin"
      ? [
          { labelKey: "settings.breadcrumb.root", href: "/settings" },
          { labelKey: "settings.nav.administration", href: "/settings/admin" },
          { labelKey: "settings.nav.adminSkills", href: "/settings/admin/skills" },
          { labelKey: "settings.skills.create.title" },
        ]
      : [
          { labelKey: "settings.breadcrumb.root", href: "/settings" },
          { labelKey: "settings.nav.skills", href: "/settings/skills" },
          { labelKey: "settings.skills.create.title" },
        ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-8 pt-8 pb-10">
        <div className="mx-auto max-w-[880px] space-y-6">
          <SettingsPageHeader
            breadcrumbs={breadcrumbs}
            titleKey="settings.skills.create.title"
            backHref={basePath}
            titleTrailing={<ScopeBadge scope={form.scope} />}
            trailing={
              <Button onClick={handleCreate} disabled={submitting}>
                {submitting ? <Spinner size="sm" /> : null}
                {t("settings.skills.create.button")}
              </Button>
            }
          />

          {submitError && (
            <div className="rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-[13px] text-destructive">
              {submitError}
            </div>
          )}

          <div className="grid gap-6">
            <FieldBlock label={t("settings.skills.detail.nameLabel")}>
              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  handleFieldChange("name", event.target.value)
                }
                placeholder={t("settings.skills.placeholder.name")}
                className={INPUT_CLASS}
              />
            </FieldBlock>

            <FieldBlock
              label={t("settings.skills.detail.descriptionLabel")}
              trailing={
                <span
                  className={cn(
                    "text-[12px] text-muted-foreground tabular-nums",
                    form.description.length >= SKILL_DESCRIPTION_MAX_LENGTH &&
                      "text-destructive",
                  )}
                >
                  {form.description.length}/{SKILL_DESCRIPTION_MAX_LENGTH}
                </span>
              }
            >
              <textarea
                value={form.description}
                onChange={(event) =>
                  handleFieldChange("description", event.target.value)
                }
                placeholder={t("settings.skills.placeholder.description")}
                spellCheck={false}
                rows={3}
                maxLength={SKILL_DESCRIPTION_MAX_LENGTH}
                className={cn(
                  INPUT_CLASS,
                  "min-h-[80px] resize-y font-mono text-[13px]",
                )}
              />
            </FieldBlock>

            <FieldBlock
              label={t("settings.skills.detail.bodyLabel")}
              trailing={
                <span
                  className={cn(
                    "text-[12px] text-muted-foreground tabular-nums",
                    form.body.length >= SKILL_BODY_MAX_LENGTH &&
                      "text-destructive",
                  )}
                >
                  {form.body.length}/{SKILL_BODY_MAX_LENGTH}
                </span>
              }
            >
              <textarea
                value={form.body}
                onChange={(event) =>
                  handleFieldChange("body", event.target.value)
                }
                placeholder={t("settings.skills.placeholder.body")}
                spellCheck={false}
                rows={18}
                maxLength={SKILL_BODY_MAX_LENGTH}
                className={cn(
                  INPUT_CLASS,
                  "min-h-[360px] resize-y font-mono text-[13px] leading-relaxed",
                )}
              />
            </FieldBlock>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldBlock({
  label,
  trailing,
  children,
}: {
  label: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="font-medium text-[13.5px] text-foreground">{label}</div>
        {trailing}
      </div>
      {children}
    </section>
  );
}
